import { useCallback, useEffect, useMemo, useState } from 'react';
import { IconPhoto, IconSearch } from '@tabler/icons-react';
import * as Sentry from '@sentry/react';
import { AlertMessage, Button, Loader } from 'ui';

import { createGalleryImageUploadUrl, updateGalleryImage, uploadGalleryImage } from '../api';
import { STATUS_STATES } from '../core/constants';
import {
  GALLERY_ACCEPTED_FILE_TYPES,
  GALLERY_MAX_BATCH,
  GALLERY_MAX_FILE_SIZE,
  GALLERY_UPLOAD_FAILED_MESSAGE,
  isGalleryEmpty,
  needsDescription,
  readImageDimensions,
} from '../core/utils/images';
import { useGallery } from '../store/gallery/hooks';
import ImageDetailsDrawer from '../components/images/ImageDetailsDrawer';
import ImageGridSkeleton from '../components/images/ImageGridSkeleton';
import ImageTile from '../components/images/ImageTile';
import ImageUploadProgressTile from '../components/images/ImageUploadProgressTile';
import NeedsDescriptionBanner from '../components/images/NeedsDescriptionBanner';
import FileDrop from '../components/file-drop/FileDrop';

/**
 * Cap on concurrent description generations. Each is a vision-model call, and a
 * 20-image drop would otherwise fire 20 at once and risk Bedrock throttling.
 */
const DESCRIPTION_CONCURRENCY = 3;

/** Run `worker` over `items`, at most `limit` at a time. */
const mapWithLimit = async (items, limit, worker) => {
  const queue = [...items];
  const runners = Array.from({ length: Math.min(limit, queue.length) }, async () => {
    while (queue.length) {
      const item = queue.shift();
      // eslint-disable-next-line no-await-in-loop
      await worker(item);
    }
  });
  await Promise.all(runners);
};

const Images = () => {
  const {
    status,
    statusDescription,
    images,
    total,
    hasMore,
    undescribedCount,
    selectedImageId,
    describingImageIds,
    view,
    setStatus,
    setView,
    selectImage,
    getImages,
    updateImage,
    generateDescription,
  } = useGallery();

  const [searchInput, setSearchInput] = useState(view.search);
  // Keyed by a local upload id so several files can report progress at once.
  const [uploads, setUploads] = useState([]);
  // Tracks only drawer saves/deletes. Deliberately NOT the page-level `loading`
  // flag: a background list refresh would otherwise grey out the drawer's Save
  // button for a reason that has nothing to do with the user's edit.
  const [mutating, setMutating] = useState(false);

  useEffect(() => {
    getImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.search, view.filter, view.page]);

  // Debounce the search box so typing does not fire a request per keystroke.
  useEffect(() => {
    if (searchInput === view.search) return undefined;
    const timer = setTimeout(() => setView({ search: searchInput }), 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const selectedImage = useMemo(
    () => images.find((image) => image.imageId === selectedImageId) ?? null,
    [images, selectedImageId],
  );

  /**
   * The undescribed count with in-flight generations taken out, so an image the
   * system is already describing is not also reported as needing the tenant's
   * attention.
   *
   * Only images that are BOTH describing and currently undescribed are subtracted:
   * a forced regeneration runs on an image that already has a description, so it
   * was never in `undescribedCount` to begin with.
   */
  const awaitingDescriptionCount = useMemo(() => {
    const inFlightUndescribed = images.filter(
      (image) => describingImageIds.includes(image.imageId) && needsDescription(image),
    ).length;
    return Math.max(0, undescribedCount - inFlightUndescribed);
  }, [images, describingImageIds, undescribedCount]);

  const patchUpload = (uploadId, patch) =>
    setUploads((current) => current.map((item) => (item.id === uploadId ? { ...item, ...patch } : item)));

  const handleFiles = useCallback(
    async (selected) => {
      // Clear any banner from a previous batch so a stale "1 of 2 uploads
      // failed" cannot sit above a retry that is already succeeding.
      setStatus({ status: STATUS_STATES.idle });

      // FileDrop enforces type and size but has no file-count limit, so the
      // batch cap is applied here — each file costs a presign, an upload, a
      // confirm and a vision call.
      const files = selected.slice(0, GALLERY_MAX_BATCH);
      if (selected.length > files.length) {
        setStatus({
          status: STATUS_STATES.error,
          statusDescription: `Only the first ${GALLERY_MAX_BATCH} images were added. Upload the rest separately.`,
        });
      }

      const queued = files.map((file) => ({
        id: `${Date.now()}-${file.name}-${Math.random().toString(36).slice(2, 8)}`,
        name: file.name,
        file,
        progress: 0,
        error: '',
      }));
      setUploads((current) => [...queued, ...current]);

      const results = await Promise.allSettled(
        queued.map(async (upload) => {
          // Dimensions are required by the presign, and the browser is the only
          // place that can read them before the bytes reach S3.
          const { width, height } = await readImageDimensions(upload.file);

          const { url, fields, image } = await createGalleryImageUploadUrl({
            filename: upload.file.name,
            contentType: upload.file.type,
            size: upload.file.size,
            width,
            height,
          });

          await uploadGalleryImage(url, fields, upload.file, (progress) => patchUpload(upload.id, { progress }));

          // Only a confirmed record is visible in the gallery; an unconfirmed one
          // is reaped by the table's TTL.
          await updateGalleryImage(image.imageId, { confirmed: true });

          return image.imageId;
        }),
      );

      const failed = results.filter((result) => result.status === 'rejected');
      results.forEach((result, index) => {
        if (result.status !== 'rejected') return;
        Sentry.captureException(result.reason);
        // The real reason is a raw HTTP string — keep it in Sentry, not on screen.
        patchUpload(queued[index].id, { error: GALLERY_UPLOAD_FAILED_MESSAGE });
      });

      // Clear the tiles that succeeded and refresh from the server.
      const failedIds = new Set(
        results.map((result, index) => (result.status === 'rejected' ? queued[index].id : null)).filter(Boolean),
      );
      setUploads((current) => current.filter((item) => failedIds.has(item.id)));

      if (failed.length < queued.length) {
        await getImages();

        // Describe the new images. Deliberately after the refresh so each tile is
        // already on screen and can show its own "Writing description" state, and
        // capped so a large drop does not fire a vision call per image at once.
        const newImageIds = results
          .filter((result) => result.status === 'fulfilled' && result.value)
          .map((result) => result.value);

        await mapWithLimit(newImageIds, DESCRIPTION_CONCURRENCY, (imageId) => generateDescription({ imageId }));
      }
      if (failed.length) {
        setStatus({
          status: STATUS_STATES.error,
          statusDescription: `${failed.length} of ${queued.length} uploads failed.`,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleSaveDescription = async (description) => {
    setMutating(true);
    try {
      await updateImage({ imageId: selectedImage.imageId, description });
    } finally {
      setMutating(false);
    }
  };

  const loading = status === STATUS_STATES.loading;
  const error = status === STATUS_STATES.error;
  /**
   * A fetch with nothing on screen yet — the first load, or a search/filter change
   * that cleared the grid. Distinct from a background refresh (or "Load more"),
   * where images are already visible and swapping them for placeholders would be
   * worse than leaving them alone under a spinner.
   */
  const initialLoading = loading && images.length === 0;
  const isEmptyLibrary = isGalleryEmpty({
    loading,
    total,
    uploadCount: uploads.length,
    search: view.search,
    filter: view.filter,
  });

  return (
    <>
      <main className="flex w-full flex-col space-y-5">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-h3">Image Library</h1>

          <div className="flex items-center gap-3">
            <div className="relative">
              <IconSearch size={16} className="absolute left-3 top-2.5 text-gray-300" />
              <input
                type="search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search by description"
                aria-label="Search image descriptions"
                className="w-[220px] rounded-lg border border-gray-100 bg-white-100 py-2 pl-9 pr-3 text-base placeholder:text-gray-300 focus:border-primary-500 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {error && !!statusDescription && (
          <AlertMessage
            open={error && !!statusDescription}
            message={statusDescription}
            severity="error"
            onClose={() => setStatus({ status: STATUS_STATES.idle })}
          />
        )}

        <NeedsDescriptionBanner count={awaitingDescriptionCount} describingCount={describingImageIds.length} />

        {/* Persistent full-width uploader above the grid, matching the supporting
            documents upload on the Organization Profile page. Kept outside the
            grid so it reads as a toolbar rather than as a tile implying the
            library is empty. */}
        <FileDrop
          onChange={handleFiles}
          allowedFileTypes={GALLERY_ACCEPTED_FILE_TYPES}
          containerClassName="!w-full !h-[122px]"
          shortDescription={`JPEG, PNG, GIF or WebP files, max size 5MB. Up to ${GALLERY_MAX_BATCH} at a time.`}
          maxFileSize={GALLERY_MAX_FILE_SIZE}
          multiple
        />

        <div className="relative min-h-40 w-full">
          {initialLoading ? (
            <ImageGridSkeleton />
          ) : isEmptyLibrary ? (
            <div className="rounded-lg border border-gray-100 bg-white-100 p-12 text-center shadow-custom-sm">
              <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-primary-50">
                <IconPhoto size={26} className="text-primary-600" />
              </div>
              <p className="text-h5">No images yet</p>
              <p className="mx-auto mt-2 max-w-md text-base text-gray-600">
                Upload photos of your work, your team, and your events using the box above. Add a short description to
                each one and we&apos;ll use them in the emails we write for you.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3">
                {uploads.map((upload) => (
                  <ImageUploadProgressTile
                    key={upload.id}
                    name={upload.name}
                    progress={upload.progress}
                    error={upload.error}
                  />
                ))}

                {images.map((image) => (
                  <ImageTile
                    key={image.imageId}
                    image={image}
                    selected={image.imageId === selectedImageId}
                    describing={describingImageIds.includes(image.imageId)}
                    onSelect={selectImage}
                  />
                ))}
              </div>

              {images.length === 0 && !loading && (
                <p className="py-10 text-center text-base text-gray-600">No images match your search.</p>
              )}

              {hasMore && (
                <div className="mt-5 flex justify-center">
                  <Button
                    title={`Load more (showing ${images.length} of ${total})`}
                    variant="outlined"
                    onClick={() => setView({ page: view.page + 1 })}
                    disabled={loading}
                  />
                </div>
              )}
            </>
          )}

          {/* The skeleton already conveys the first load; a spinner on top of it
              would be two loading indicators for one fetch. */}
          {loading && !initialLoading && <Loader />}
        </div>
      </main>

      {/* Overlays the page, matching ContactDrawer / CampaignDrawer, so the grid
          keeps its full width instead of being squeezed beside a panel. */}
      <ImageDetailsDrawer
        open={!!selectedImage}
        image={selectedImage}
        saving={mutating}
        describing={!!selectedImage && describingImageIds.includes(selectedImage.imageId)}
        onClose={() => selectImage(null)}
        onSave={handleSaveDescription}
        onGenerate={() => generateDescription({ imageId: selectedImage.imageId, force: true })}
      />
    </>
  );
};

export default Images;
